import React, { useRef } from 'react';
import { 
  ArrowLeft, Phone, RefreshCw, MoreVertical, 
  Store, User, Shield, ChevronDown, Package,
  Star, Flag 
} from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

const ChatHeader = ({
  navigate,
  isCurrentUserBuyer,
  isCurrentUserSeller,
  sellerId,
  sellerName,
  sellerStoreLogo,
  sellerStoreName,
  buyerName,
  buyerProfilePicture,
  listingTitle,
  listingPrice,
  listingImage,
  activeEscrow,
  handleManualRefresh,
  showDropdown,
  setShowDropdown,
  setShowRateModal,
  setShowReportModal,
  setShowEscrowModal
}) => {
  const dropdownRef = useRef(null);
  
  // Determine what to display based on user role
  const displayName = isCurrentUserBuyer 
    ? (sellerStoreName || sellerName || 'Seller')
    : (buyerName || 'Buyer');

  const displayAvatar = isCurrentUserBuyer 
    ? sellerStoreLogo || getImageUrl(sellerName?.charAt(0) || 'S')
    : buyerProfilePicture || getImageUrl(buyerName?.charAt(0) || 'B');

  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <header className="bg-white shadow sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Top Row */}
        <div className="py-3 flex items-center justify-between">
          {/* Left side: Back button */}
          <button
            onClick={() => navigate('/chats')}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          {/* Center: Store/Buyer Info */}
          <div className="flex items-center gap-3 flex-1 justify-center">
            {/* Avatar */}
            {displayAvatar ? (
              <img 
                src={displayAvatar}
                alt={displayName}
                className="w-8 h-8 rounded-full object-cover border border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.nextElementSibling;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            
            {/* Fallback Avatar */}
            <div className={`${displayAvatar ? 'hidden' : 'flex'} w-8 h-8 rounded-full items-center justify-center text-white font-bold ${
              isCurrentUserBuyer 
                ? 'bg-gradient-to-r from-orange-500 to-red-500' 
                : 'bg-gradient-to-r from-blue-500 to-blue-600'
            }`}>
              {displayName?.charAt(0).toUpperCase() || 'U'}
            </div>
            
            {/* Name with appropriate icon */}
            <div className="flex flex-col items-start">
              <span className="font-semibold text-gray-900">{displayName}</span>
              <div className="flex items-center gap-1">
                {isCurrentUserBuyer ? (
                  <>
                    <button
                      onClick={() => navigate(`/seller/${sellerId}`)}
                      className="flex items-center gap-1 hover:text-orange-600 transition-colors text-xs text-gray-500"
                    >
                      <Store className="w-3 h-3" />
                      <span>View Store</span>
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <User className="w-3 h-3" />
                    <span>Buyer</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Right side: Action buttons */}
          <div className="flex items-center gap-2">
            {/* Call button (only for buyer) */}
            {isCurrentUserBuyer && (
              <button
                onClick={() => alert('Call feature coming soon!')}
                className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                title="Call seller"
              >
                <Phone className="w-5 h-5" />
              </button>
            )}
            
            {/* Refresh escrow */}
            <button
              onClick={handleManualRefresh}
              className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
              title="Refresh escrow status"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            
            {/* More options dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
              
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {isCurrentUserBuyer ? (
                    // Buyer options
                    <>
                      <button
                        onClick={() => {
                          setShowRateModal(true);
                          setShowDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Star className="w-4 h-4" />
                        Rate Seller
                      </button>
                      <button
                        onClick={() => {
                          setShowReportModal(true);
                          setShowDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Flag className="w-4 h-4" />
                        Report Seller
                      </button>
                    </>
                  ) : (
                    // Seller options
                    <>
                      <button
                        onClick={() => {
                          // Add seller-specific actions here
                          setShowDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Flag className="w-4 h-4" />
                        Report Buyer
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Bottom Row: Listing Info */}
        <div className="py-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Listing Image */}
              {listingImage ? (
                <img
                  src={getImageUrl(listingImage)} 
                  alt={listingTitle}
                  className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                  <Package className="w-5 h-5 text-gray-400" />
                </div>
              )}
              
              {/* Listing Details */}
              <div>
                <p className="text-sm font-medium text-gray-900 line-clamp-1">
                  {listingTitle || 'Item Listing'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatPrice(listingPrice || activeEscrow?.amount || 0)}
                </p>
              </div>
            </div>
            
            {/* Secure Payment Button */}
            <button
              onClick={() => setShowEscrowModal(true)}
              className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 px-4 py-2 rounded-lg transition-colors border border-green-200"
            >
              <Shield className="w-4 h-4" />
              <span className="font-medium text-sm">Secure Payment</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default ChatHeader;